import React, { useEffect } from 'react';
import { InputTextarea } from 'primereact/inputtextarea';
import { Checkbox } from 'primereact/checkbox';

const DetailedDevelopmentPlan = ({ formik, index, cellStyle, cellStyleW, autoCompetencyName }) => {
    const basePath = `leadership_reviews_and_development_plan.development_plan.priorities.${index}`;
    const values = formik.values.leadership_reviews_and_development_plan?.development_plan?.priorities?.[index] || {};

    const handleChange = (field, value) => {
        formik.setFieldValue(`${basePath}.${field}`, value);
    };

    // Auto-fill competency name if derived from summary table
    useEffect(() => {
        if (autoCompetencyName && values.competency_name !== autoCompetencyName) {
            handleChange('competency_name', autoCompetencyName);
        }
    }, [autoCompetencyName]);

    return (
        <div className="mb-8 border-2 border-gray-400 p-4 bg-white shadow-md">
            <h3 className="text-xl font-bold mb-4 bg-yellow-100 p-2 border-l-4 border-yellow-500">
                Priority #{index + 1}
            </h3>

            {/* Competency Name */}
            <div className="mb-4">
                <label className="block font-bold mb-1">Competency Name:</label>
                <input
                    type="text"
                    className="w-full p-2 border border-gray-300 bg-gray-100"
                    placeholder="Enter Competency Name..."
                    value={values.competency_name || ''}
                    readOnly={!!autoCompetencyName} // Make it read-only if auto-filled to prevent conflict
                    onChange={(e) => handleChange('competency_name', e.target.value)}
                />
            </div>

            {/* Why this is a priority */}
            <div className="mb-4">
                <label className="block font-bold mb-1">
                    Why this is a priority (based on scores & evidence):
                    <span className="block text-sm font-normal text-gray-600">Reference specific weak statements, recurring weekly scorecard items, performance points, and real project examples.</span>
                </label>
                <InputTextarea
                    rows={5}
                    className="w-full p-2 border border-gray-300"
                    placeholder="Provide detailed evidence..."
                    value={values.why_priority || ''}
                    onChange={(e) => handleChange('why_priority', e.target.value)}
                />
            </div>

            {/* 12-Month Target State */}
            <div className="mb-4">
                <label className="block font-bold mb-1">12-Month Target State</label>
                <p className="text-sm italic text-gray-700 mb-2">
                    "By this time next year, this competency will consistently score 8+ and will no longer appear as a recurring issue in scorecards, client feedback, or project outcomes."
                </p>
                <label className="block font-semibold mb-1 text-sm">Specific description of what “8-level” performance looks like:</label>
                <InputTextarea
                    rows={4}
                    className="w-full p-2 border border-gray-300"
                    placeholder="Compare current state to target state..."
                    value={values.target_state_description || ''}
                    onChange={(e) => handleChange('target_state_description', e.target.value)}
                />
            </div>

            {/* Specific Behavior Changes Required */}
            <div className="mb-4">
                <label className="block font-bold mb-2">Specific Behavior Changes Required (Observable)</label>
                {[0, 1, 2].map(i => (
                    <div key={i} className="flex items-center gap-2 mb-2">
                        <span className="font-bold">{i + 1}.</span>
                        <input
                            type="text"
                            className="w-full p-2 border border-gray-300"
                            placeholder="Observable behavior..."
                            value={values.behavior_changes?.[i] || ''}
                            onChange={(e) => handleChange(`behavior_changes.${i}`, e.target.value)}
                        />
                    </div>
                ))}
            </div>

            {/* How We Will Measure Improvement */}
            <div className="mb-4">
                <label className="block font-bold mb-2">How We Will Measure Improvement</label>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3 pl-2">
                     <div className="flex items-center">
                        <Checkbox 
                            inputId={`cb1-${index}`} 
                            checked={values.measurement_checkboxes?.weekly_scorecard || false} 
                            onChange={e => handleChange('measurement_checkboxes.weekly_scorecard', e.checked)} 
                        />
                        <label htmlFor={`cb1-${index}`} className="ml-2">Weekly scorecard trend improvement</label>
                     </div>
                     <div className="flex items-center">
                        <Checkbox 
                            inputId={`cb3-${index}`} 
                            checked={values.measurement_checkboxes?.client_feedback || false} 
                            onChange={e => handleChange('measurement_checkboxes.client_feedback', e.checked)} 
                        />
                        <label htmlFor={`cb3-${index}`} className="ml-2">Client feedback improvement</label>
                     </div>
                     <div className="flex items-center">
                        <Checkbox 
                            inputId={`cb4-${index}`} 
                            checked={values.measurement_checkboxes?.fewer_escalations || false} 
                            onChange={e => handleChange('measurement_checkboxes.fewer_escalations', e.checked)} 
                        />
                        <label htmlFor={`cb4-${index}`} className="ml-2">Fewer escalations</label>
                     </div>
                     <div className="flex items-center">
                        <Checkbox 
                            inputId={`cb5-${index}`} 
                            checked={values.measurement_checkboxes?.supervisor_observation || false} 
                            onChange={e => handleChange('measurement_checkboxes.supervisor_observation', e.checked)} 
                        />
                        <label htmlFor={`cb5-${index}`} className="ml-2">Supervisor observation</label>
                     </div>
                     <div className="flex items-center">
                        <Checkbox 
                            inputId={`cb6-${index}`} 
                            checked={values.measurement_checkboxes?.project_results || false} 
                            onChange={e => handleChange('measurement_checkboxes.project_results', e.checked)} 
                        />
                        <label htmlFor={`cb6-${index}`} className="ml-2">Project results (schedule, margin)</label>
                     </div>
                      <div className="flex items-center col-span-2">
                        <Checkbox 
                            inputId={`cb2-${index}`} 
                            checked={values.measurement_checkboxes?.fewer_recurring_issues || false} 
                            onChange={e => handleChange('measurement_checkboxes.fewer_recurring_issues', e.checked)} 
                        />
                        <label htmlFor={`cb2-${index}`} className="ml-2 whitespace-nowrap mr-2">Fewer recurring issues in: </label>
                        <input 
                            type="text" 
                            className="border-b border-gray-400 focus:outline-none flex-grow"
                            value={values.measurement_checkboxes?.fewer_recurring_issues_text || ''}
                            onChange={(e) => handleChange('measurement_checkboxes.fewer_recurring_issues_text', e.target.value)}
                        />
                     </div>
                </div>

                <label className="block font-semibold mb-1 text-sm mt-2">Specific proof we expect to see:</label>
                <InputTextarea
                    rows={3}
                    className="w-full p-2 border border-gray-300"
                    placeholder="Proof descriptions..."
                    value={values.specific_proof || ''}
                    onChange={(e) => handleChange('specific_proof', e.target.value)}
                />
            </div>

            {/* Quarterly Checkpoints */}
             <div className="mb-4">
                <label className="block font-bold mb-2 bg-gray-200 p-1">Quarterly Checkpoints</label>
                <div className="w-full">
                    <div className="mb-4">
                         <label className="font-semibold block text-sm">Q1 Focus:</label>
                         <InputTextarea rows={3} style={{ width: '100%', boxSizing: 'border-box' }} className="w-full p-2 border border-gray-300" value={values.quarterly_focus?.q1 || ''} onChange={(e) => handleChange('quarterly_focus.q1', e.target.value)} />
                    </div>
                     <div className="mb-4">
                         <label className="font-semibold block text-sm">Q2 Focus:</label>
                         <InputTextarea rows={3} style={{ width: '100%', boxSizing: 'border-box' }} className="w-full p-2 border border-gray-300" value={values.quarterly_focus?.q2 || ''} onChange={(e) => handleChange('quarterly_focus.q2', e.target.value)} />
                    </div>
                     <div className="mb-4">
                         <label className="font-semibold block text-sm">Q3 Focus:</label>
                         <InputTextarea rows={3} style={{ width: '100%', boxSizing: 'border-box' }} className="w-full p-2 border border-gray-300" value={values.quarterly_focus?.q3 || ''} onChange={(e) => handleChange('quarterly_focus.q3', e.target.value)} />
                    </div>
                     <div className="mb-4">
                         <label className="font-semibold block text-sm">Q4 Focus:</label>
                         <InputTextarea rows={3} style={{ width: '100%', boxSizing: 'border-box' }} className="w-full p-2 border border-gray-300" value={values.quarterly_focus?.q4 || ''} onChange={(e) => handleChange('quarterly_focus.q4', e.target.value)} />
                    </div>
                </div>
             </div>
        </div>
    );
};

export default DetailedDevelopmentPlan;
